/**
 * `/buddy`: the AI Buddy module (canon `docs/design/ai-buddy-2026-09-30/`, boards N, M, P, Q, R).
 *
 * The page owns the header (the module's name, the AI note R49 asks for, and „Usuń rozmowę"), the
 * history rail beside the conversation, the conversation's four states, the catalogue writes for
 * an exercise outside it („Szybkie dodanie", and the creator for „Zweryfikuj i dodaj"), the session
 * panel over a draft, and the hand-over of a finished draft to the builder.
 *
 * ── One component for every conversation ─────────────────────────────────────────────────────
 * Which conversation is open is the address's answer (`model/search.ts`): `?rozmowa=<id>` for one
 * from the history, and otherwise the conversation this device is in for the recipient (its draft
 * key, a new one when there is none). Stepping between conversations changes that answer and
 * nothing else, so the rail stays mounted, keeps its scroll, and reads the history once. Only the
 * thread is keyed by the conversation, so one conversation's entrances and picks never show in
 * another.
 *
 * ── The stage holds its box while the reads run ──────────────────────────────────────────────
 * Buddy's status and the conversation are read before anything stands on the stage: the hero or
 * the thread depends on whether this conversation already has turns, and drawing the hero first
 * would swap it for a thread the moment a resumed conversation landed. The header and the rail's
 * frame stand from the first frame; the stage beside them waits, gated, and fills once.
 *
 * ── Opening the draft ────────────────────────────────────────────────────────────────────────
 * Every unresolved name decided, the plan is read with the decisions in it (`resolvedPlan`), through
 * the builder's own reader (`readPlanDocument`), adopted as a new unsaved plan that names this
 * conversation (`adoptAiDraft`), and seeded (`seedNewPlan`) for `/plans/nowy`. The first Zapisz (or
 * the assignment, for a client) then binds the conversation (`entities/plan-draft`'s
 * `bindAiDraft`). A bound conversation lets its draft key go.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useId, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import {
  aiStatusQueryOptions,
  conversationHandle,
  currentDraftKey,
  forgetDraftKey,
  newConversationKey,
  resolvedPlan,
  useBuddyEntry,
  type AiBuildResult,
  type AiNamedDecision,
  type AiUnresolvedExercise,
  type BuddyEntry,
} from '~/entities/ai-conversation';
import { clientIdentityQueryOptions } from '~/entities/client';
import {
  adoptAiDraft,
  discardLeftoverPlan,
  readNewPlanEntry,
  readPlanDocument,
  resumeLeftoverPlan,
  seedNewPlan,
  type LeftoverPlan,
  type PlanDraft,
} from '~/entities/plan-draft';
import { useSessionUser } from '~/entities/session';
import { ApiError } from '~/shared/api/mutator';
import { BuddyChat, HistoryRail, useBuddyConversation, type QuickAddOutcome } from '~/features/ai-chat';
import {
  CreateExerciseDialog,
  addProposedExercise,
  invalidateExerciseLibrary,
  type ExerciseCreatorRequest,
} from '~/features/create-exercise';
import { ResumePlanDialog } from '~/features/create-plan';
import { preferenceStore } from '~/shared/lib/preferenceStore';
import { useDocumentTitle } from '~/shared/lib/useDocumentTitle';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { BrandLoader } from '~/shared/ui/BrandLoader';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { EmptyState } from '~/shared/ui/EmptyState';
import { IconTooltip } from '~/shared/ui/IconTooltip';
import { PearlMark } from '~/shared/ui/PearlMark';
import { SidebarIcon } from '~/shared/ui/SidebarIcon';
import ghost from '~/shared/ui/ghostButton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { draftDocument } from '../lib/draftDocument';
import { useBuddyHistory } from '../lib/useBuddyHistory';
import { useBuddySessionDock } from '../lib/useBuddySessionDock';
import { draftSessionGroups } from '../model/draftSessions';
import { readHistoryRailOpen, writeHistoryRailOpen } from '../model/historyRail';

import { BuddySessionDock } from './BuddySessionDock';
import styles from './AiPlanBuilderScreen.module.css';

export function AiPlanBuilderScreen() {
  const { t } = useTranslation();
  useDocumentTitle(t('web:aiPlan.module.title'));
  const userId = useSessionUser()?.id ?? null;

  return (
    <main className={styles.page}>
      {userId === null ? (
        <>
          <PageHeader onDelete={null} history={null} />
          <div className={styles.body} aria-busy="true" />
        </>
      ) : (
        <BuddyModule userId={userId} />
      )}
    </main>
  );
}

/**
 * The 56px header: the history rail's sidebar toggle, the module's name, the AI note R49 asks for,
 * and the one act on the conversation as a whole, „Usuń rozmowę", which only a conversation with
 * turns has.
 *
 * The toggle's place is kept the same way while there is no rail to open (before the session, outside
 * the beta, while the session panel holds the rail's column), so the title never moves.
 *
 * The button's place is kept from the first frame, whether it can act or not: it stands after the
 * note, so drawing it only once a resumed conversation landed would push the note sideways. Until
 * then (`onDelete` is `null`) it is invisible and out of reach of every input.
 */
function PageHeader({
  onDelete,
  history,
  titleRef,
}: {
  readonly onDelete: (() => void) | null;
  /** The history rail's toggle: whether it stands open, the rail's id, and the press; `null` while there is none to open. */
  readonly history: { readonly open: boolean; readonly railId: string; readonly onToggle: () => void } | null;
  readonly titleRef?: RefObject<HTMLHeadingElement | null>;
}) {
  const { t } = useTranslation();
  const toggleLabel = t(history?.open === true ? 'web:aiPlan.history.hide' : 'web:aiPlan.history.show');
  const toggle = (
    <button
      type="button"
      className={`${String(styles.railToggle)} ${history === null ? String(styles.deleteReserved) : ''}`}
      inert={history === null}
      aria-hidden={history === null ? 'true' : undefined}
      aria-label={toggleLabel}
      aria-expanded={history?.open ?? false}
      aria-controls={history?.railId}
      onClick={history?.onToggle}
    >
      <SidebarIcon className={styles.railToggleIcon} />
    </button>
  );
  return (
    <header className={styles.header}>
      {history === null ? toggle : <IconTooltip label={toggleLabel}>{toggle}</IconTooltip>}
      <h1 ref={titleRef} tabIndex={-1} className={styles.title}>
        {t('web:aiPlan.module.title')}
      </h1>
      <span className={styles.spacer} />
      <p className={styles.note}>{t('web:aiPlan.header.note')}</p>
      <button
        type="button"
        className={`${String(ghost.ghost)} ${String(styles.delete)} ${onDelete === null ? String(styles.deleteReserved) : ''}`}
        inert={onDelete === null}
        aria-hidden={onDelete === null ? 'true' : undefined}
        onClick={onDelete ?? undefined}
      >
        {t('web:aiPlan.delete.action')}
      </button>
    </header>
  );
}

/** Why Buddy cannot start anything now: one sentence, said the same on the stage and in the rail. */
function unavailableWords(t: TFunction, entry: BuddyEntry): string {
  return entry === 'switchedOff' || entry === 'serviceCeiling' ? t(`web:aiPlan.entry.${entry}`) : t('web:aiPlan.unavailable.title');
}

/** Whether a read was refused because the client is not this trainer's (any more). */
function notOnTheRoster(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 403);
}

function BuddyModule({ userId }: { readonly userId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/_shell/buddy' });
  const { klient, rozmowa, szkic, nazwa } = search;
  const recipientKey = klient ?? null;
  const fromHistory = rozmowa !== undefined;

  // The conversation this device is in for the recipient: its draft key. For a client it is minted
  // only once the client is read (below), so an address naming somebody else's client keeps and
  // creates nothing. Read again whenever the recipient the address names changes (a client's „Plan
  // z Buddym" arriving on an open module).
  const keyFor = (recipientId: number | null) => currentDraftKey(preferenceStore(), userId, recipientId, () => crypto.randomUUID());
  const [slot, setSlot] = useState<{ readonly recipientKey: number | null; readonly draftKey: string | null }>(() => ({
    recipientKey,
    draftKey: recipientKey === null ? keyFor(null) : null,
  }));
  if (slot.recipientKey !== recipientKey) setSlot({ recipientKey, draftKey: recipientKey === null ? keyFor(null) : null });
  const draftKey = slot.recipientKey === recipientKey ? slot.draftKey : null;
  const renewSlot = (mint: 'fresh' | 'current') => {
    const store = preferenceStore();
    const next = () => crypto.randomUUID();
    setSlot({
      recipientKey,
      draftKey: mint === 'fresh' ? newConversationKey(store, userId, recipientKey, next) : currentDraftKey(store, userId, recipientKey, next),
    });
  };

  const buddy = useBuddyConversation(
    fromHistory ? { kind: 'id', conversationId: rozmowa } : { kind: 'key', draftKey, clientId: recipientKey },
    userId,
  );
  const handle = fromHistory ? conversationHandle(rozmowa) : (draftKey ?? '');
  // The status this page reads is remembered for the navigation's entry.
  const entry = useBuddyEntry(userId);
  const status = useQuery(aiStatusQueryOptions());
  const statusKnown = status.data !== undefined;

  const conversation = buddy.state.phase === 'ready' ? buddy.state.conversation : null;
  // Who the plan is for is the CONVERSATION's own answer, set when it was created. Until it is
  // read, a conversation started here is for the client the address names.
  const client = conversation !== null ? (conversation.clientId ?? undefined) : fromHistory ? undefined : klient;
  const recipient = useQuery({ ...clientIdentityQueryOptions(client ?? 0), enabled: client !== undefined });
  // A client the trainer no longer has: the conversation still opens, for a neutral „były klient".
  const formerClient = client !== undefined && recipient.data === undefined && notOnTheRoster(recipient.error);
  if (!fromHistory && recipientKey !== null && draftKey === null && recipient.data !== undefined) {
    setSlot({ recipientKey, draftKey: keyFor(recipientKey) });
  }
  // A refetch that fails over a name already read keeps the conversation; only a first read that
  // never answered is the failure state. A client no longer on the roster is not a failure.
  const recipientFailed = client !== undefined && recipient.isError && recipient.data === undefined && !formerClient;
  // A new conversation for a client who is not this trainer's: there is nothing to start.
  const recipientGone = !fromHistory && conversation === null && formerClient;
  const gate = useSkeletonGate(
    buddy.state.phase === 'pending' || (client !== undefined && recipient.isPending && !recipient.isError),
  );

  // Outside the beta there is no history to show, and no rail; until the status says, the rail waits.
  const railShown = !(statusKnown && entry === 'hidden');
  // Hidden until the trainer opens it, and remembered on this browser (owner, 10 October 2026).
  const [railOpen, setRailOpen] = useState(() => readHistoryRailOpen(preferenceStore()));
  // The rail fades in only when the trainer opens it; restored open from the preference it stands still.
  const [railToggled, setRailToggled] = useState(false);
  const toggleRail = () => {
    const next = !railOpen;
    setRailToggled(true);
    setRailOpen(next);
    writeHistoryRailOpen(preferenceStore(), next);
  };
  const railId = useId();
  const history = useBuddyHistory({
    readable: statusKnown && entry !== 'hidden',
    statusRetry:
      !statusKnown && status.isError
        ? () => {
            void status.refetch();
          }
        : null,
    conversation,
  });
  const dock = useBuddySessionDock(conversation, handle);

  // The conversation's draft became a saved template: it is done, and this address starts afresh.
  // Let go here, where the bind is seen, rather than when the draft opened: a draft opened and
  // never saved comes back to the same conversation. Decided during render (the key a render
  // reads must never be one already retired), once per bound key. A conversation opened from the
  // history stays as it is: it is read where it stands.
  const bound = !fromHistory && draftKey !== null && buddy.state.phase === 'ready' && buddy.state.boundTemplateId !== null;
  if (bound) {
    forgetDraftKey(preferenceStore(), userId, recipientKey, draftKey);
    renewSlot('current');
  }

  // „Usuń rozmowę": asked in the page, never through `window.confirm`. A deleted conversation lets
  // its draft key go, and the address starts afresh on the hero.
  const [deleteAsked, setDeleteAsked] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // The button that asked is gone with the turns, so a confirmed delete hands focus to the title.
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [deleted, setDeleted] = useState(false);
  const confirmDelete = async () => {
    setDeleting(true);
    const removed = await buddy.remove();
    setDeleting(false);
    setDeleted(removed);
    setDeleteAsked(false);
    if (!removed) return;
    dock.letGo();
    history.refresh();
    if (fromHistory) {
      // The device may be in this conversation under its key too: let that key go, or the next
      // visit would start an empty conversation under a key whose conversation is gone.
      if (conversation !== null && conversation.draftKey !== null) forgetDraftKey(preferenceStore(), userId, conversation.clientId, conversation.draftKey);
      void navigate({ to: '/buddy', search: {}, replace: true });
      return;
    }
    if (draftKey !== null) forgetDraftKey(preferenceStore(), userId, recipientKey, draftKey);
    renewSlot('current');
  };
  const deletable = conversation !== null && conversation.turns.length > 0;

  // „Nowa rozmowa": a conversation of its own, in the library; the open one is left where it is,
  // in the history. On a conversation with nothing said yet there is nothing to leave, so the
  // press only goes back to it. Either way the composer takes focus once it stands. What the
  // session panel holds unsaved is saved first, or asked about.
  const [composerFocus, setComposerFocus] = useState<{ readonly handle: string; readonly press: number } | null>(null);
  const startNew = () => {
    const start = () => {
      const emptyLibrarySlot = !fromHistory && recipientKey === null && conversation !== null && conversation.turns.length === 0;
      const nextHandle = emptyLibrarySlot ? handle : newConversationKey(preferenceStore(), userId, null, () => crypto.randomUUID());
      if (!emptyLibrarySlot && recipientKey === null) setSlot({ recipientKey: null, draftKey: nextHandle });
      setComposerFocus((current) => ({ handle: nextHandle, press: (current?.press ?? 0) + 1 }));
      void navigate({ to: '/buddy', search: {} });
    };
    void dock.flush().then(({ settled }) => {
      if (settled) start();
      else dock.askToLeave(start);
    });
  };

  // „Zweryfikuj i dodaj": the creator over the page, and the row's wait for its answer — the saved
  // exercise's id, or `null` when it closed unsaved. The list makes the pick; nothing is decided here.
  const [creator, setCreator] = useState<{ readonly request: ExerciseCreatorRequest; readonly opener: HTMLElement | null } | null>(null);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const creatorAnswer = useRef<((exerciseId: number | null) => void) | null>(null);
  const answerCreator = (exerciseId: number | null) => {
    creatorAnswer.current?.(exerciseId);
    creatorAnswer.current = null;
  };

  // Unsaved work already on this device is asked about first, as „+ Nowy program" asks: the one
  // unsaved plan the device keeps is never replaced without the trainer's say.
  const [leftover, setLeftover] = useState<{ readonly plan: LeftoverPlan; readonly draft: PlanDraft } | null>(null);
  const [leftoverOpen, setLeftoverOpen] = useState(false);

  // A seed replaces the document the session panel edits, so the panel lets it go first.
  const seed = (draft: PlanDraft) => {
    dock.letGo();
    seedNewPlan(
      draft,
      client !== undefined && recipient.data !== undefined ? { clientId: client, clientName: recipient.data.name } : null,
      userId,
    );
    void navigate({ to: '/plans/nowy' });
  };

  const openDraft = async (turnId: number, shown: AiBuildResult) => {
    if (conversation === null) return;
    // What was edited in the session panel is saved to the draft first, so the builder opens the
    // draft as the trainer left it. An edit that cannot be saved is asked about: the draft opens
    // without it only on the trainer's say.
    const { settled, draft: onServer } = await dock.flush(turnId);
    const open = () => {
      const plan = resolvedPlan(onServer ?? shown);
      const read = plan === null ? null : readPlanDocument(plan);
      if (read === null || read.outcome !== 'draft' || (client !== undefined && recipient.data === undefined && !formerClient)) {
        buddy.report('open');
        return;
      }
      const adopted = adoptAiDraft(read.draft, conversation.id, fromHistory ? conversation.draftKey : draftKey);
      const entryOnDevice = readNewPlanEntry(userId);
      if (entryOnDevice.kind === 'resume') {
        setLeftover({ plan: entryOnDevice.plan, draft: adopted });
        setLeftoverOpen(true);
        return;
      }
      seed(adopted);
    };
    if (settled) open();
    else dock.askToLeave(open);
  };

  // A decision rewrites the draft on the server, so what was edited in the session panel goes there
  // first, and edits made while it is on its way are sent on the revision it answers with. A decision
  // is never made over edits that could not be saved: they would be lost under the draft it answers
  // with.
  // Answers whether the decision landed: the list changes its rows only then.
  const decide = (turnId: number, decisions: readonly AiNamedDecision[]): Promise<boolean> =>
    dock.beforeDecision(turnId).then(async (handBack) => {
      if (handBack === null) {
        buddy.report('decisionHeld');
        return false;
      }
      const revision = await buddy.decide(turnId, decisions);
      handBack(revision);
      return revision !== null;
    });

  // „Szybkie dodanie" and „Dodaj wszystkie szybko" (R8, board E): each proposal added to the
  // catalogue as it stands, one after another, marked for review by the server. A failed add leaves
  // its row waiting; the list picks the added ones in one resolution. The catalogue is read again
  // once, for however many were added.
  const quickAdd = async (exercises: readonly AiUnresolvedExercise[]): Promise<QuickAddOutcome> => {
    const added: AiNamedDecision[] = [];
    const failed: QuickAddOutcome['failed'][number][] = [];
    for (const exercise of exercises) {
      try {
        const exerciseId = await addProposedExercise(exercise.name, exercise.proposedExercise);
        added.push({ name: exercise.name, decision: { kind: 'use', exerciseId } });
      } catch (error) {
        // The answer's status says why (the list words it); no answer at all is the network's.
        failed.push({ name: exercise.name, status: error instanceof ApiError ? error.status : null });
      }
    }
    if (added.length > 0) void invalidateExerciseLibrary(queryClient);
    return { added, failed };
  };

  const recipientName = client === undefined ? null : formerClient ? t('web:aiPlan.formerClient') : (recipient.data?.name ?? null);

  let content;
  if (gate.waiting) {
    // A slow read shows the brand's own wait in the stage; a quick one shows nothing at all.
    content = gate.skeleton ? (
      <div className={styles.state}>
        <BrandLoader size="panel" label={t('web:aiPlan.loading')} />
      </div>
    ) : (
      <span className={hidden.srOnly}>{t('web:aiPlan.loading')}</span>
    );
  } else if (buddy.state.phase === 'gone' || recipientGone) {
    const clientGone = recipientGone || (buddy.state.phase === 'gone' && buddy.state.what === 'client');
    content = (
      <div className={`${String(styles.state)} ${gate.reveal}`}>
        <EmptyState
          mark={<PearlMark className={styles.stateMark} />}
          tone="neutral"
          title={clientGone ? t('web:aiPlan.gone.client') : t('web:aiPlan.gone.title')}
          hint={clientGone ? t('web:aiPlan.gone.clientHint') : t('web:aiPlan.gone.hint')}
        />
        <Link to="/buddy" search={{}} replace className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}>
          {t('web:aiPlan.gone.action')}
        </Link>
      </div>
    );
  } else if (buddy.state.phase === 'unavailable') {
    content = (
      <div className={`${String(styles.state)} ${gate.reveal}`}>
        <EmptyState
          mark={<PearlMark className={styles.stateMark} />}
          tone="neutral"
          title={unavailableWords(t, entry)}
          hint={t('web:aiPlan.unavailable.hint')}
        />
        <Link to="/plans" className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}>
          {t('web:aiPlan.unavailable.action')}
        </Link>
      </div>
    );
  } else if (buddy.state.phase === 'failed' || recipientFailed) {
    const retry =
      buddy.state.phase === 'failed'
        ? buddy.state.retry
        : () => {
            void recipient.refetch();
          };
    content = (
      <div className={`${String(styles.state)} ${gate.reveal}`}>
        <EmptyState
          mark={<PearlMark className={styles.stateMark} />}
          tone="neutral"
          title={recipientFailed ? t('web:aiPlan.recipientFailed') : t('web:aiPlan.readFailed')}
        />
        <button type="button" className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`} onClick={retry}>
          {t('web:aiPlan.failed.retry')}
        </button>
      </div>
    );
  } else if (conversation !== null) {
    content = (
      <div className={`${String(styles.stage)} ${gate.reveal}`}>
        {recipientName !== null && <p className={styles.recipient}>{t('web:aiPlan.recipient', { name: recipientName })}</p>}
        <BuddyChat
          key={conversation.id}
          conversation={conversation}
          onSend={(intent) => buddy.send(intent, intent.kind === 'message' ? dock.draftForMessage() : undefined)}
          onStop={buddy.stop}
          onDismissOutline={buddy.dismissOutline}
          deciding={buddy.deciding}
          openRow={szkic !== undefined && nazwa !== undefined ? { turnId: szkic, name: nazwa } : null}
          exercises={{
            decide,
            catalogue: {
              kind: 'here',
              quickAdd: (_turnId, exercises) => quickAdd(exercises),
              verify: (_turnId, exercise, opener) =>
                new Promise<number | null>((resolve) => {
                  // „Zweryfikuj i dodaj": the normal creator, filled from the proposal (or with the
                  // name alone); its save is the pick. Buddy never creates the exercise itself.
                  answerCreator(null);
                  creatorAnswer.current = resolve;
                  setCreator({
                    opener,
                    request: {
                      mode: 'create',
                      source: null,
                      opening: Date.now(),
                      proposed: { name: exercise.name, proposal: exercise.proposedExercise },
                    },
                  });
                  setCreatorOpen(true);
                }),
            },
          }}
          onOpenDraft={(turnId, result) => {
            void openDraft(turnId, result);
          }}
          sessionsOf={(_turnId, result) => {
            const draft = draftDocument(result);
            return draft === null ? [] : draftSessionGroups(draft, result);
          }}
          openSession={dock.open}
          onOpenSession={dock.openSession}
          returnFocusSession={dock.returnFocusToken}
          focusComposer={composerFocus !== null && composerFocus.handle === handle ? composerFocus.press : null}
          onFeedback={buddy.rate}
          notice={buddy.notice}
          proposalHost={dock.proposalHost}
          onDecideChanges={(turnId, version, decisions) => {
            void buddy.decideChanges(turnId, version, decisions);
          }}
          importer={entry === 'offered' ? { list: buddy.listFile, send: buddy.importFile } : null}
        />
      </div>
    );
  }

  const dockOpen = dock.open !== null;
  return (
    <>
      <PageHeader
        titleRef={titleRef}
        history={railShown && !dockOpen ? { open: railOpen, railId, onToggle: toggleRail } : null}
        onDelete={
          deletable
            ? () => {
                setDeleted(false);
                setDeleteAsked(true);
              }
            : null
        }
      />
      <div className={styles.body}>
        {/* The rail gives its column to the session panel while one is open (board R): the
          * conversation keeps a readable width beside the 574px panel. It stays mounted, out of
          * the flow and out of reach, so it keeps its scroll for when the panel closes. Closed by
          * the header's toggle it is `hidden`, still mounted, for the same scroll. */}
        {railShown && (
          <div id={railId} className={dockOpen ? styles.railBesideDock : `${String(styles.railColumn)} ${railToggled ? String(styles.railEnters) : ''}`} inert={dockOpen} hidden={!railOpen}>
            <HistoryRail
              state={history.state}
              clientName={history.clientName}
              openConversationId={conversation !== null && conversation.turns.length > 0 ? conversation.id : null}
              onNew={entry === 'offered' ? startNew : null}
              newRefusal={entry === 'offered' ? null : unavailableWords(t, entry)}
              now={history.now}
              timeZone={history.timeZone}
            />
          </div>
        )}
        <div
          className={`${String(styles.stage)} ${dockOpen ? String(styles.stageBesideDock) : ''}`}
          aria-busy={gate.waiting ? 'true' : undefined}
        >
          {content}
        </div>
      </div>

      <BuddySessionDock
        dock={dock}
        onOpenInBuilder={() => {
          const turn = dock.open === null ? null : conversation?.turns.find((each) => each.id === dock.open?.turnId);
          if (turn?.result?.kind === 'build') void openDraft(turn.id, turn.result.result);
        }}
      />
      <ConfirmDialog
        prompt={
          deleteAsked
            ? {
                title: t('web:aiPlan.delete.title'),
                description: t('web:aiPlan.delete.description'),
                confirmLabel: t('web:aiPlan.delete.confirm'),
                cancelLabel: t('web:aiPlan.delete.cancel'),
                destructiveConfirm: true,
                busy: deleting,
              }
            : null
        }
        onConfirm={() => {
          void confirmDelete();
        }}
        onCancel={() => {
          setDeleteAsked(false);
        }}
        finalFocus={deleted ? titleRef : undefined}
      />
      <ResumePlanDialog
        open={leftoverOpen}
        plan={leftover?.plan ?? null}
        handingOver={false}
        finalFocus={() => null}
        onDismiss={() => {
          setLeftoverOpen(false);
        }}
        onContinue={() => {
          setLeftoverOpen(false);
          if (leftover === null) return;
          dock.letGo();
          resumeLeftoverPlan(leftover.plan, userId);
          void navigate({ to: '/plans/nowy' });
        }}
        onStartFresh={() => {
          setLeftoverOpen(false);
          if (leftover === null) return;
          discardLeftoverPlan(leftover.plan, userId);
          seed(leftover.draft);
        }}
        onClosed={() => undefined}
      />
      <CreateExerciseDialog
        open={creatorOpen}
        request={creator?.request ?? null}
        onClose={() => {
          setCreatorOpen(false);
          answerCreator(null);
        }}
        onSaved={(result) => {
          setCreatorOpen(false);
          answerCreator(result.exerciseId);
        }}
        // Back to the row's „Zweryfikuj i dodaj"; once its decision lands the row hands focus to its header.
        finalFocus={() => (creator?.opener?.isConnected === true ? creator.opener : true)}
      />
    </>
  );
}
